import { KeyboardEvent, useState } from "react";
import { isValidEmail } from "../../helper/common";
import { Button, Icon, RowButton, Select, TextField } from "../design";
import styles from "./GuarantorInviteSection.module.css";

export type GuarantorInviteMethod = "email" | "self";

export type GuarantorInvite = {
  name: string;
  email: string;
  guarantorMethod: GuarantorInviteMethod;
  relation: string;
};

type GuarantorInviteSectionProps = {
  currentUserEmail?: string;
  invites: GuarantorInvite[];
  onChange: (invites: GuarantorInvite[]) => void;
  _t: (key: string, context?: string) => string;
  variant?: "application" | "join";
  guarantorOptionalKey?: string;
};

const GuarantorInviteSection = ({
  currentUserEmail,
  invites,
  onChange,
  _t,
  variant = "application",
  guarantorOptionalKey,
}: GuarantorInviteSectionProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [method, setMethod] = useState<GuarantorInviteMethod>("email");
  const [relation, setRelation] = useState("");
  const [error, setError] = useState("");

  const resetForm = () => {
    setName("");
    setEmail("");
    setMethod("email");
    setRelation("");
    setError("");
    setIsOpen(false);
  };

  const addGuarantor = () => {
    const trimmedName = name.trim();
    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedName) {
      setError(_t("nameRequired"));
      return;
    }
    if (!isValidEmail(trimmedEmail)) {
      setError(_t("invalidEmail"));
      return;
    }
    if (!relation) {
      setError(
        _t("guarantorRelationRequired", "Guarantor relation is required")
      );
      return;
    }
    if (trimmedEmail === currentUserEmail?.toLowerCase()) {
      setError(_t("cannotAddYourself"));
      return;
    }
    if (invites.some((invite) => invite.email === trimmedEmail)) {
      setError(_t("emailAlreadyAdded"));
      return;
    }

    onChange([
      ...invites,
      {
        name: trimmedName,
        email: trimmedEmail,
        guarantorMethod: method,
        relation,
      },
    ]);
    resetForm();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      addGuarantor();
    }
  };

  return (
    <div
      className={`${styles.section} ${
        variant === "join" ? styles.joinVariant : ""
      }`}
    >
      {invites.map((invite) => (
        <div className={styles.addedPerson} key={invite.email}>
          <span className={styles.avatar}>{invite.name.charAt(0)}</span>
          <div className={styles.personDetails}>
            <strong>{invite.name}</strong>
            <p>
              {invite.email} · {_t("guarantor")} ·{" "}
              {invite.guarantorMethod === "self"
                ? _t("illFillItIn", "I'll fill it in")
                : _t("inviteByEmail", "Send email")}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            iconStart={<Icon name="close" />}
            onClick={() =>
              onChange(invites.filter(({ email }) => email !== invite.email))
            }
            aria-label={_t("removeGuarantor")}
          />
        </div>
      ))}

      {isOpen ? (
        <div
          className={`${styles.form} ${
            method === "self" ? styles.selfMethod : ""
          }`}
        >
          <div className={styles.formHeader}>
            <span>{_t("guarantor")}</span>
          </div>
          <TextField
            id="guarantor-invite-name"
            className={styles.nameInput}
            value={name}
            autoFocus
            onChange={(event) => {
              setName(event.target.value);
              setError("");
            }}
            onBeforeInput={(event) => {
              const data = (event.nativeEvent as InputEvent).data;
              if (data && !/^[A-Za-zÀ-ÿ\s'-]+$/.test(data)) {
                event.preventDefault();
              }
            }}
            onKeyDown={handleKeyDown}
            placeholder={_t("fullName")}
            aria-label={_t("fullName")}
          />
          <TextField
            id="guarantor-invite-email"
            className={styles.emailInput}
            type="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value.toLowerCase());
              setError("");
            }}
            onKeyDown={handleKeyDown}
            placeholder="email@example.com"
            aria-label={_t("email")}
          />
          {method === "email" && (
            <Select
              id="guarantor-invite-relation"
              className={styles.relation}
              value={relation}
              onChange={(event) => {
                setRelation(event.target.value);
                setError("");
              }}
              aria-label={_t("guarantorRelation", "Guarantor relation")}
              placeholder={_t("guarantorRelation", "Guarantor relation")}
              options={[
                { value: "parent", label: _t("parent") },
                { value: "Family", label: _t("Family") },
                { value: "relative", label: _t("relative") },
                { value: "partner", label: _t("partner") },
                { value: "company", label: _t("company") },
                { value: "other", label: _t("other") },
              ]}
            />
          )}
          <Select
            id="guarantor-invite-method"
            className={styles.method}
            value={method}
            onChange={(event) => {
              const nextMethod = event.target.value as GuarantorInviteMethod;
              setMethod(nextMethod);
              setError("");
            }}
            aria-label={_t("guarantorAddMethod")}
            options={[
              { value: "email", label: _t("inviteByEmail") },
              { value: "self", label: _t("illFillItIn") },
            ]}
          />
          <Button
            className={styles.addButton}
            size="lg"
            block
            onClick={addGuarantor}
            disabled={!name.trim() || !email.trim() || !relation}
          >
            {_t("add")}
          </Button>
          <Button
            className={styles.closeButton}
            variant="ghost"
            size="sm"
            iconOnly
            iconStart={<img src="/images/delete-icon.svg" alt="" />}
            onClick={resetForm}
            aria-label={_t("remove")}
          />
          {error && <p className={styles.error}>{error}</p>}
        </div>
      ) : (
        <RowButton
          className={styles.openButton}
          onClick={() => setIsOpen(true)}
        >
          <span className={styles.addIcon}>+</span>
          <strong>{_t("addAGuarantor")}</strong>
          <em>· {_t(guarantorOptionalKey || "guarantorOptional")}</em>
        </RowButton>
      )}
    </div>
  );
};

export default GuarantorInviteSection;
